'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@coas/ui/lib';
import dynamic from 'next/dynamic';
import { memo } from 'react';
import type { RouterOutputs } from 'trpc/shared';
import { ProductFilterTriggerFallback } from '~/features/ProductFilter';
import { SubCategories } from '~/features/SubCategories';
import IconChevronRight from '~/shared/assets/icons/chevron-right.svg';
import { useHeaderOffset, useSubcategoryRef } from '../helpers/hooks';

const ProductSorting = dynamic(() => import('~/features/ProductSorting').then(module => module.ProductSorting), {
    loading: () => (
        <div
            className={cn(
                'flex flex-1 items-center justify-center gap-x-1 py-2.5 text-center text-transparent',
                'md:px-6 md:text-14',
                'max-md:font-700 max-md:text-16',
            )}
        >
            <span className={'animate-pulse rounded-6 bg-neutral-300'}>...</span>
            <IconChevronRight className={'mb-0.5 hidden size-3.5 stroke-neutral-400 md:block'} />
        </div>
    ),
    ssr: false,
});
const ProductFilterTrigger = dynamic(
    () => import('~/features/ProductFilter').then(module => module.ProductFilterTrigger),
    {
        loading: () => (
            <ProductFilterTriggerFallback className={'flex-1 py-2.5 text-center font-700 text-16 text-neutral-500'} />
        ),
        ssr: false,
    },
);

type ProductGridHeaderProps = {
    category: RouterOutputs['category']['getBySlug'] | null;
};

const ProductGridHeader: React.FC<ProductGridHeaderProps> = memo(({ category }) => {
    const t = useTranslations('productGrid');
    const subcategoryRef = useSubcategoryRef();
    const headerOffset = useHeaderOffset({ subcategoryRef });

    return (
        <div className={'sticky z-50'} style={{ top: headerOffset }}>
            {!!category && <SubCategories ref={subcategoryRef} category={category} className={'bg-neutral-50'} />}
            <div className={'relative flex w-full shrink-0 items-center bg-neutral-200 md:hidden'}>
                <ProductSorting />
                <div className={'h-5 w-px bg-neutral-300'} />
                <ProductFilterTrigger className={'flex-1 py-2.5 text-center font-700 text-16 text-neutral-500'}>
                    {t('filters')}
                </ProductFilterTrigger>
            </div>
            <div className={'hidden w-full shrink-0 items-center justify-center bg-neutral-200 md:flex'}>
                <div
                    className={'grid'}
                    style={{
                        gridTemplateColumns: 'repeat(5, auto)',
                    }}
                >
                    <ProductSorting />
                    <ProductFilterTrigger className={'flex-1 py-2.5 text-center font-700 text-16 text-neutral-500'}>
                        {t('filters')}
                    </ProductFilterTrigger>
                </div>
            </div>
        </div>
    );
});
ProductGridHeader.displayName = 'ProductGridHeader';

export { ProductGridHeader };
